import FolderIcon from '@mui/icons-material/Folder'
import { useMediaQuery } from '@mui/material'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { round, some } from 'lodash'
import moment from 'moment-timezone'
import { FC } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DocumentIconName } from '../../../../components/DocumentIconName'
import { File, Folder } from '../../../../swagger/dtoGateway'
import { theme } from '../../../../theme'
import { getFileName, getFileType } from '../../../../utils/files'
import { FileItemMenu } from './FileItemMenu'
import { FileUploaderButton } from './FileUploaderButton'

const ts = Intl.DateTimeFormat().resolvedOptions().timeZone

const desktopColumns = [
    { id: 'title', label: 'Название', align: 'left' },
    { id: 'type', label: 'Тип файла', align: 'left' },
    { id: 'size', label: 'Размер', align: 'left' },
    {
        id: 'date',
        label: 'Дата изменения',
        align: 'left',
    },
]

const mobileColumns = [
    { id: 'title', label: 'Название', align: 'left' },
]

type Props = {
    files: Folder[]
    folders?: File[]
    idEntity: string
    isReadOnly?: boolean
    isShowButton?: boolean
    isChoiceFilesDialog: boolean
}

export const FileTableList: FC<Props> = ({
    files,
    folders = [],
    idEntity,
    isReadOnly,
    isShowButton = true,
    isChoiceFilesDialog,
    choiceFiles,
    handleClickChoiceFiles,
}) => {
    const [searchParams, setSearchParams] = useSearchParams()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))

    const columns = isMobile ? mobileColumns : desktopColumns

    const handleClickFolder = (name: string) => {
        const folder = decodeURI(name.split('disk').pop() ?? '')
        searchParams.set('folder', folder)
        setSearchParams(searchParams)
    }

    const handleClickTableCell = (file) => {
        if (isChoiceFilesDialog) {
            handleClickChoiceFiles(file)
        } else {
            window.open(file.url)
        }
    }

    return (
        <>
            {!isReadOnly && isShowButton && <FileUploaderButton />}
            <TableContainer sx={{ width: '100%', overflowY: 'auto', p: 0 }}>
                <Table
                    sx={{
                        borderCollapse: 'separate',
                        borderSpacing: ' 0 8px',
                        boxSizing: 'border-box',
                    }}>
                    <TableHead sx={{ borderTop: 'none' }}>
                        <TableRow sx={{ background: '#F5F5F5' }}>
                            {columns.map((column, index) => (
                                <TableCell
                                    align={column.align}
                                    sx={{ backgroundColor: '#F4F4F6', color: '#5A5F72' }}
                                    key={column.label}
                                    style={{
                                        borderTopLeftRadius: index === 0 ? '16px' : 0,
                                    }}>
                                    {column.label}
                                </TableCell>
                            ))}
                            <TableCell
                                sx={{
                                    backgroundColor: '#F4F4F6',
                                    color: '#5A5F72',
                                    borderTopRightRadius: '16px',
                                }}
                            />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[...folders, ...files].map((file) => {
                            const isFolder = file.object_type === 'folder'

                            const fileName = getFileName(file.filename)
                            const fileType = getFileType(file.filename)
                            const isChoiceFiles = some(choiceFiles, (f) => f.url === file.url)

                            return (
                                <>
                                    <TableRow key={file.filename}>
                                        <TableCell
                                            onClick={() =>
                                                isFolder
                                                    ? handleClickFolder(file.filename)
                                                    : !isReadOnly && handleClickTableCell(file)
                                            }
                                            component="th"
                                            scope="row"
                                            sx={{
                                                padding: '0 8px ',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: isMobile ? '200px' : '600px',
                                                cursor: 'pointer',
                                                borderTopLeftRadius: '12px',
                                                borderBottomLeftRadius: '12px',
                                                borderTop: isChoiceFiles
                                                    ? '1px solid #006CFD'
                                                    : '1px solid transparent',
                                                borderBottom: isChoiceFiles
                                                    ? '1px solid #006CFD'
                                                    : '1px solid transparent',
                                                borderLeft: isChoiceFiles
                                                    ? '1px solid #006CFD'
                                                    : '1px solid transparent',
                                            }}>
                                            {isFolder ? (
                                                <Stack
                                                    direction="row"
                                                    sx={{ alignItems: 'center' }}
                                                    spacing={1}>
                                                    <FolderIcon
                                                        sx={{ color: '#004B9F', fontSize: '30px' }}
                                                    />
                                                    <Typography variant="caption2">
                                                        {decodeURI(
                                                            file.filename
                                                                ?.slice(0, -1)
                                                                .split('/')
                                                                .pop(),
                                                        )}
                                                    </Typography>
                                                </Stack>
                                            ) : (
                                                <DocumentIconName
                                                    name={fileName}
                                                    isWrapper={false}
                                                    type={fileType}
                                                />
                                            )}
                                        </TableCell>

                                        {!isMobile ? (
                                            <TableCell
                                                sx={{
                                                    borderTop: isChoiceFiles
                                                        ? '1px solid #006CFD'
                                                        : '1px solid transparent',
                                                    borderBottom: isChoiceFiles
                                                        ? '1px solid #006CFD'
                                                        : '1px solid transparent',
                                                }}>
                                                {isFolder ? 'Папка' : fileType}
                                            </TableCell>
                                        ) : null}
                                        {!isMobile && (
                                            <TableCell
                                                sx={{
                                                    borderTop: isChoiceFiles
                                                        ? '1px solid #006CFD'
                                                        : '1px solid transparent',
                                                    borderBottom: isChoiceFiles
                                                        ? '1px solid #006CFD'
                                                        : '1px solid transparent',
                                                }}>
                                                {round(file.size / (1024 * 1024), 3)} МБ
                                            </TableCell>
                                        )}

                                        {!isMobile ? (
                                            <TableCell
                                                sx={{
                                                    borderTop: isChoiceFiles
                                                        ? '1px solid #006CFD'
                                                        : '1px solid transparent',
                                                    borderBottom: isChoiceFiles
                                                        ? '1px solid #006CFD'
                                                        : '1px solid transparent',
                                                }}>
                                                {file.last_modified &&
                                                    moment(file.last_modified)
                                                        .tz(ts)
                                                        .format('DD.MM.YY HH:mm')}
                                            </TableCell>
                                        ) : null}
                                        <TableCell
                                            align="right"
                                            sx={{
                                                position: 'relative',
                                                borderTop: isChoiceFiles
                                                    ? '1px solid #006CFD'
                                                    : '1px solid transparent',
                                                borderRight: isChoiceFiles
                                                    ? '1px solid #006CFD'
                                                    : '1px solid transparent',
                                                borderBottom: isChoiceFiles
                                                    ? '1px solid #006CFD'
                                                    : '1px solid transparent',
                                                borderTopRightRadius: '12px',
                                                borderBottomRightRadius: '12px',
                                            }}>
                                            {!isReadOnly && !isChoiceFilesDialog && (
                                                <FileItemMenu file={file} idEntity={idEntity} />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow
                                        colSpan={4}
                                        sx={{
                                            height: '1px',
                                        }}>
                                        <TableCell
                                            colSpan={6}
                                            sx={{
                                                padding: 0,
                                            }}
                                        />
                                    </TableRow>
                                </>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    )
}
